import { useEffect, useMemo, useState } from 'react'
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import { FormExperienceScreen } from '../../Forms/FormExperienceScreen'
import { useAuth } from '../../../context/useAuth'
import { useToast } from '../../../context/useToast'
import { useFormDefinition, useFormDefinitions } from '../../../hooks/useFormDefinitions'
import {
  DEBUG_MEMBER_FORM_DEFINITIONS,
  sendFormRequest,
  subscribeAssignedFormRequests,
  subscribeFormRequestMembers,
  subscribeSentFormRequests,
  type FormRequestMember,
} from '../../../services/formRequestService'
import { isNoSignInPreviewActive } from '../../../debug'
import { testingOverrideAllowsAllFeatures } from '../../../config/testingOverrides'
import { setGhostEchoDirty } from '../../../utils/ghostEchoDirtyRegistry'
import type { WorkspaceRole } from '../../../context/WorkspaceContext'
import { roleCanPerform } from '../../../utils/workspacePermissions'
import type { FormDefinition, FormRequest } from '../../../types/forms'
import { Fab } from '../../Common/Fab'
import { StatusChip, TextInput } from '../../Common'
import {
  MemberActionCard,
  MemberBottomSheet,
  MemberEmptyState,
  MemberHeroHeader,
  MemberSegmented,
  type MemberActionCardTone,
} from './MemberControlsPrimitives'

import { isFormRequestOverdue, toDate } from './MemberFormsMobile.helpers'

type FormsSegment = 'ASSIGNED' | 'SENT'

const relativeTime = (value: FormRequest['requestedAt']) => {
  const date = toDate(value)
  if (!date) return 'date pending'
  const diff = Date.now() - date.getTime()
  const minutes = Math.max(0, Math.round(diff / 60_000))
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

const getRequestStatus = (request: FormRequest): { label: string; tone: MemberActionCardTone } => {
  if (isFormRequestOverdue(request)) return { label: 'Overdue', tone: 'danger' }
  if (request.status === 'COMPLETED') return { label: 'Submitted', tone: 'success' }
  if (request.status === 'EXPIRED') return { label: 'Overdue', tone: 'danger' }
  if (request.status === 'CANCELLED') return { label: 'Cancelled', tone: 'neutral' }
  return { label: 'Pending', tone: 'warning' }
}

const EMPTY_MEMBERS: FormRequestMember[] = []
const EMPTY_SENT: FormRequest[] = []

export const MemberFormsMobile = ({ workspaceId, role }: { workspaceId: string; role: WorkspaceRole }) => {
  const { currentUser } = useAuth()
  const { showToast } = useToast()
  const canAssign = roleCanPerform(role, 'EDITOR') || testingOverrideAllowsAllFeatures()
  const activeFormsPage = useFormDefinitions({ workspaceId, lifecycleStatus: 'ACTIVE', search: '', sort: 'NEWEST', cursor: null })
  const [members, setMembers] = useState<FormRequestMember[]>([])
  const [assigned, setAssigned] = useState<FormRequest[]>([])
  const [sent, setSent] = useState<FormRequest[]>([])
  const [segment, setSegment] = useState<FormsSegment>('ASSIGNED')
  const [assignOpen, setAssignOpen] = useState(false)
  const [formSearch, setFormSearch] = useState('')
  const [selectedFormId, setSelectedFormId] = useState('')
  const [selectedRecipientId, setSelectedRecipientId] = useState('')
  const [sending, setSending] = useState(false)
  const [activeRequest, setActiveRequest] = useState<FormRequest | null>(null)
  const [activeFormDirty, setActiveFormDirty] = useState(false)
  const selectedDefinition = useFormDefinition(activeRequest?.formId ?? null)

  const activeForms = isNoSignInPreviewActive() ? DEBUG_MEMBER_FORM_DEFINITIONS : activeFormsPage.items
  const visibleForms = activeForms.filter((form) => form.title.toLocaleLowerCase().includes(formSearch.trim().toLocaleLowerCase()))
  const rendererDefinition = useMemo<FormDefinition | null>(() => {
    if (!activeRequest) return null
    return DEBUG_MEMBER_FORM_DEFINITIONS.find((form) => form.id === activeRequest.formId) ?? selectedDefinition.form
  }, [activeRequest, selectedDefinition.form])

  useEffect(() => {
    setGhostEchoDirty(Boolean(activeRequest && activeFormDirty))
    return () => setGhostEchoDirty(false)
  }, [activeFormDirty, activeRequest])

  const effectiveMembers = canAssign ? members : EMPTY_MEMBERS
  const effectiveSent = canAssign ? sent : EMPTY_SENT
  const effectiveSelectedFormId = selectedFormId || activeForms[0]?.id || ''
  const effectiveRecipientId = selectedRecipientId || effectiveMembers[0]?.uid || ''

  useEffect(() => {
    if (!currentUser) return
    const stopMembers = canAssign
      ? subscribeFormRequestMembers(workspaceId, currentUser.uid, setMembers, (error) => showToast(error.message, 'error'))
      : () => undefined
    const stopAssigned = subscribeAssignedFormRequests(workspaceId, currentUser.uid, setAssigned, (error) => showToast(error.message, 'error'))
    const stopSent = canAssign
      ? subscribeSentFormRequests(workspaceId, currentUser.uid, setSent, (error) => showToast(error.message, 'error'))
      : () => undefined
    return () => { stopMembers(); stopAssigned(); stopSent() }
  }, [canAssign, currentUser, showToast, workspaceId])

  const send = async () => {
    if (!effectiveSelectedFormId || !effectiveRecipientId) return
    setSending(true)
    try {
      await sendFormRequest({ workspaceId, formId: effectiveSelectedFormId, recipientId: effectiveRecipientId })
      showToast('Form assigned.', 'success')
      setAssignOpen(false)
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not assign form.', 'error')
    } finally {
      setSending(false)
    }
  }

  const requests = segment === 'SENT' ? effectiveSent : assigned
  const overdueCount = assigned.filter((request) => isFormRequestOverdue(request)).length + effectiveSent.filter((request) => isFormRequestOverdue(request)).length

  return (
    <section className="memberControlsMobile memberFormsMobile" data-testid="member-forms-orchestrator">
      <MemberHeroHeader
        eyebrow="Forms"
        title="Assignments"
        chips={(
          <div className="memberChipCluster">
            <StatusChip size="sm" tone="neutral" className="memberMetricChip">Assigned {assigned.length}</StatusChip>
            {canAssign ? <StatusChip size="sm" tone="neutral" className="memberMetricChip">Sent {effectiveSent.length}</StatusChip> : null}
            <StatusChip size="sm" tone={overdueCount > 0 ? 'danger' : 'neutral'} className="memberMetricChip">Overdue {overdueCount}</StatusChip>
          </div>
        )}
      />

      <MemberSegmented<FormsSegment>
        value={segment}
        onChange={setSegment}
        ariaLabel="Form assignment lists"
        options={[
          { key: 'ASSIGNED', label: 'Assigned to me', count: assigned.length },
          ...(canAssign ? [{ key: 'SENT' as const, label: 'Sent by me', count: effectiveSent.length }] : []),
        ]}
      />

      {!canAssign ? <p className="memberControlHint">Viewers can submit assigned forms but cannot assign new requests.</p> : null}

      <div className="memberActionList">
        {requests.length === 0 ? (
          <MemberEmptyState
            icon={<AssignmentRoundedIcon fontSize="inherit" />}
            title={segment === 'ASSIGNED' ? 'No assigned forms' : 'No sent forms'}
            hint={segment === 'ASSIGNED' ? 'Assigned requests will appear here when they need your response.' : 'Assignments you send will appear here for follow-up.'}
          />
        ) : requests.map((request) => {
          const status = getRequestStatus(request)
          const detail = segment === 'ASSIGNED'
            ? `${request.requestedByName || 'Workspace'} · ${relativeTime(request.requestedAt)}`
            : `${request.recipientName || 'Recipient'} · ${relativeTime(request.requestedAt)}`
          return (
            <MemberActionCard
              key={request.id}
              icon={<DescriptionRoundedIcon fontSize="inherit" />}
              title={request.formTitle}
              sub={detail}
              status={status.label}
              statusTone={status.tone}
              onClick={() => segment === 'ASSIGNED' && setActiveRequest(request)}
              disabled={segment === 'SENT'}
              testId="member-request-card"
            />
          )
        })}
      </div>

      {canAssign ? (
        <Fab
          extended
          className="memberGradientFab"
          onClick={() => setAssignOpen(true)}
          icon={<PersonAddAltRoundedIcon fontSize="small" />}
          label="Assign"
        />
      ) : null}

      <MemberBottomSheet open={assignOpen} onClose={() => setAssignOpen(false)} title="Assign form" testId="member-form-assign-sheet">
        <div className="memberAssignSheet">
          <label>
            <span className="memberEyebrow">Form</span>
            <span className="memberSheetSearchWrap">
              <SearchRoundedIcon fontSize="small" />
              <TextInput className="memberSheetSearch" type="search" value={formSearch} onChange={setFormSearch} placeholder="Search forms" aria-label="Search forms" />
            </span>
          </label>
          <div className="memberActionList">
            {visibleForms.map((form) => (
              <MemberActionCard
                key={form.id}
                icon={<DescriptionRoundedIcon fontSize="inherit" />}
                title={form.title}
                sub={form.description || 'Active form'}
                status={effectiveSelectedFormId === form.id ? 'Selected' : undefined}
                statusTone="info"
                onClick={() => setSelectedFormId(form.id)}
              />
            ))}
          </div>
          <span className="memberEyebrow">Member</span>
          <div className="memberActionList">
            {effectiveMembers.map((member) => (
              <MemberActionCard
                key={member.uid}
                icon={<PersonAddAltRoundedIcon fontSize="inherit" />}
                title={member.name}
                sub={member.email || member.role}
                status={effectiveRecipientId === member.uid ? member.role : undefined}
                statusTone="info"
                onClick={() => setSelectedRecipientId(member.uid)}
              />
            ))}
          </div>
        </div>
        <Fab
          extended
          className="memberGradientFab memberGradientFab--sheet"
          disabled={!effectiveSelectedFormId || !effectiveRecipientId || sending}
          onClick={() => void send()}
          icon={<SendRoundedIcon fontSize="small" />}
          label={sending ? 'Assigning...' : 'Assign'}
        />
      </MemberBottomSheet>

      {activeRequest && rendererDefinition ? (
        <MemberBottomSheet
          open
          onClose={() => { setActiveFormDirty(false); setActiveRequest(null) }}
          title={rendererDefinition.title}
          maxHeightSvh={98}
          testId="member-form-renderer"
        >
          <FormExperienceScreen
            definition={rendererDefinition}
            initialMode="FILL"
            canRespond={activeRequest.status === 'PENDING'}
            canReviewResponses={false}
            compact
            onBack={() => {
              setActiveFormDirty(false)
              setActiveRequest(null)
            }}
            onDirtyChange={setActiveFormDirty}
            onSubmitted={() => {
              setActiveFormDirty(false)
              setActiveRequest(null)
            }}
          />
        </MemberBottomSheet>
      ) : null}
    </section>
  )
}
